import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button } from '@trove/ui';
import { fromBase64, wrapKey } from '@trove/crypto';
import type { WrappedKeyPayload } from '@trove/contracts';
import { CheckCircle, Devices, QrCode } from '@phosphor-icons/react';
import { api, postJson } from '../api';
import { EntryActions, EntryLayout, EntryNotice } from '../components/EntryLayout';
import { PairingCodeField } from '../components/PairingCodeField';
import { QrScanner } from '../components/QrScanner';
import { useWorkspace } from '../workspace';
import { isCliDeviceLabel, isOwnPairingCode } from '../devicePairing';
import { PAIRING_CODE_PATTERN, extractPairingCode, formatPairingCode } from '../pairingCode';
import styles from './connect.module.css';

type PendingDevice = {
  deviceId: string;
  deviceLabel: string;
  encryptionPublicKey: string;
};

export const Route = createFileRoute('/connect')({
  validateSearch: (search: Record<string, unknown>): { code?: string } => ({
    code:
      typeof search.code === 'string' && search.code ? formatPairingCode(search.code) : undefined,
  }),
  component: ConnectPage,
});

function ConnectPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/connect' });
  const { workspace, loading: workspaceLoading } = useWorkspace();
  const [code, setCode] = useState(search.code ?? '');
  const [pending, setPending] = useState<PendingDevice>();
  const [pendingError, setPendingError] = useState<string>();
  const [approving, setApproving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [approved, setApproved] = useState<{ label: string; keyDelivered: boolean }>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    setCode(search.code ?? '');
  }, [search.code]);

  const ready = PAIRING_CODE_PATTERN.test(code);
  const ownRequest = ready && isOwnPairingCode(code);
  const canDeliverKey = Boolean(
    workspace && (workspace.vaultMode === 'open' || Boolean(workspace.collectionKey?.length)),
  );
  const cliRequest = Boolean(pending && isCliDeviceLabel(pending.deviceLabel));
  const needsVaultKey = Boolean(pending && !cliRequest && workspace?.vaultMode !== 'open');
  const authorizeBlocked = ownRequest || (needsVaultKey && !canDeliverKey);
  const canAuthorize =
    ready && !approving && !workspaceLoading && !authorizeBlocked && !pendingError;

  useEffect(() => {
    if (!ready || ownRequest) {
      setPending(undefined);
      setPendingError(undefined);
      return;
    }

    let cancelled = false;
    void api<PendingDevice>(`/v1/auth/device/pending?code=${encodeURIComponent(code)}`)
      .then((next) => {
        if (cancelled) return;
        setPending(next);
        setPendingError(undefined);
      })
      .catch((cause) => {
        if (cancelled) return;
        setPending(undefined);
        setPendingError(
          cause instanceof Error && cause.message
            ? cause.message
            : 'That code expired or is not a device pairing code.',
        );
      });

    return () => {
      cancelled = true;
    };
  }, [code, ownRequest, ready]);

  const applyCode = (next: string) => {
    setCode(next);
    setError(undefined);
    void navigate({
      search: next ? { code: next } : {},
      replace: true,
    });
  };

  const approve = async (nextCode = code, options?: { allowWithoutKey?: boolean }) => {
    if (!PAIRING_CODE_PATTERN.test(nextCode)) return;
    if (isOwnPairingCode(nextCode)) {
      setError('This is this browser’s pairing code. Approve it from a different unlocked device.');
      return;
    }

    setApproving(true);
    setError(undefined);
    try {
      const device =
        pending && pending.deviceId
          ? pending
          : await api<PendingDevice>(
              `/v1/auth/device/pending?code=${encodeURIComponent(nextCode)}`,
            );
      const cli = isCliDeviceLabel(device.deviceLabel);
      const deliverKey = Boolean(
        workspace && (workspace.vaultMode === 'open' || workspace.collectionKey?.length),
      );
      if (!cli && workspace?.vaultMode !== 'open' && !deliverKey) {
        setError('Unlock this vault first, then authorize. Approving now would spend the code.');
        return;
      }
      if (cli && !deliverKey && !options?.allowWithoutKey) {
        setError('Unlock this vault to hand over a key, or authorize without one.');
        return;
      }

      let deviceWrap: WrappedKeyPayload | undefined;
      if (workspace?.vaultMode !== 'open' && workspace?.collectionKey?.length) {
        deviceWrap = await wrapKey(
          Uint8Array.from(workspace.collectionKey),
          fromBase64(device.encryptionPublicKey),
        );
      }

      const result = await postJson<{
        deviceLabel: string;
        keyDelivered: boolean;
      }>('/v1/auth/device/approve', {
        code: nextCode,
        ...(deviceWrap ? { deviceWrap } : {}),
        ...(workspace ? { repositoryId: workspace.repositoryId } : {}),
      });
      setApproved({
        label: result.deviceLabel,
        keyDelivered: Boolean(result.keyDelivered),
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not connect this device');
    } finally {
      setApproving(false);
    }
  };

  const onScanned = (payload: string) => {
    setScanning(false);
    const next = extractPairingCode(payload);
    if (!next) {
      setError('That QR is not a pairing code. Use the code from Connect, not a share link.');
      return;
    }
    applyCode(next);
    navigator.vibrate?.(12);
    if (isOwnPairingCode(next)) {
      setError('This is this browser’s pairing code. Approve it from a different unlocked device.');
      return;
    }
    if (!canDeliverKey) return;
    void approve(next);
  };

  const scanner = scanning ? (
    <QrScanner
      onResult={onScanned}
      onCancel={() => setScanning(false)}
      onTypeInstead={() => setScanning(false)}
    />
  ) : null;

  if (approved) {
    return (
      <EntryLayout
        visual={false}
        eyebrow={
          <span className={styles.successIcon}>
            <CheckCircle size={20} weight="fill" />
          </span>
        }
        title={`${approved.label} is connected`}
        description={
          approved.keyDelivered
            ? 'Go back to that device — it will open your files on its own.'
            : 'That device will ask for your backup phrase to unlock your files.'
        }
      >
        <EntryActions>
          <Button variant="primary" onClick={() => void navigate({ to: '/' })}>
            Back to files
          </Button>
        </EntryActions>
      </EntryLayout>
    );
  }

  return (
    <EntryLayout
      visual={false}
      title="Connect a device"
      description="Scan the QR code shown on the other device, or type its one-time code."
    >
      <div className={styles.codeBlock}>
        <PairingCodeField
          value={code}
          disabled={approving}
          onValueChange={applyCode}
          onSubmit={() => void approve()}
        />
        <Button
          className={styles.scan}
          disabled={approving}
          onClick={() => {
            setError(undefined);
            setScanning(true);
          }}
        >
          <QrCode size={16} />
          Scan QR code
        </Button>
      </div>

      {ownRequest ? (
        <EntryNotice tone="danger">
          This is this browser’s own pairing code. Approve it from a different device that’s already
          unlocked.
        </EntryNotice>
      ) : pending ? (
        <div className={styles.pending}>
          <Devices size={16} />
          <span>
            Request from <strong>{pending.deviceLabel}</strong>
          </span>
        </div>
      ) : null}

      {!workspaceLoading && !canDeliverKey && !ownRequest ? (
        <EntryNotice>
          {cliRequest
            ? 'Unlock this vault first to hand the CLI a key. You can still authorize without one — it will ask for the backup phrase.'
            : 'Unlock your files on this browser first. Approving now would use up the code without unlocking the other device.'}
        </EntryNotice>
      ) : null}

      {pendingError ? <EntryNotice tone="danger">{pendingError}</EntryNotice> : null}
      {error ? <EntryNotice tone="danger">{error}</EntryNotice> : null}

      <EntryActions>
        {cliRequest && !canDeliverKey ? (
          <Button
            variant="primary"
            disabled={
              !ready || approving || workspaceLoading || ownRequest || Boolean(pendingError)
            }
            onClick={() => void approve(code, { allowWithoutKey: true })}
          >
            {approving ? 'Connecting…' : 'Authorize without key'}
          </Button>
        ) : (
          <Button variant="primary" disabled={!canAuthorize} onClick={() => void approve()}>
            {approving ? 'Connecting…' : pending ? `Approve ${pending.deviceLabel}` : 'Approve'}
          </Button>
        )}
        <Button variant="ghost" onClick={() => void navigate({ to: '/' })}>
          Cancel
        </Button>
      </EntryActions>
      {scanner}
    </EntryLayout>
  );
}
